import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Modal from '../../components/Common/Modal';
import PropertyPreferences from '../../components/Customers/PropertyPreferences';
import InterestedPortfolios from '../../components/Customers/InterestedPortfolios';
import api from '../../utils/api';
import { showSuccess, showDeleteConfirm } from '../../utils/swal';
import { formatStatus } from '../../utils/formatters';
import {
  ArrowLeft,
  Edit,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Calendar,
  DollarSign,
  MessageSquare,
  Plus,
  Save,
  X,
  Trash2,
  Users
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAssign } = usePermission();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('info');

  // Editing states
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});

  const [editingRegistration, setEditingRegistration] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({});

  // Contact modal
  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ type: 'GSM', value: '' });

  // Meeting modal
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    date: '',
    type: 'Telefon',
    description: ''
  });

  // Note form
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await api.get(`/customers/${id}`);
      const customerData = response.data;

      console.log('Raw customer data:', customerData);
      console.log('Raw contacts:', customerData.contacts);
      console.log('Contacts type:', typeof customerData.contacts);

      // Contacts array ise (backend'den relation olarak geliyorsa)
      if (Array.isArray(customerData.contacts)) {
        // Zaten array, olduğu gibi kullan
        console.log('Contacts is already array');
      }
      // Contacts JSON string ise parse et
      else if (customerData.contacts && typeof customerData.contacts === 'string') {
        try {
          customerData.contacts = JSON.parse(customerData.contacts);
          console.log('Parsed contacts from string:', customerData.contacts);
        } catch (e) {
          console.error('Contacts parse error:', e);
          customerData.contacts = [];
        }
      }
      // Contacts yoksa boş array
      else if (!customerData.contacts) {
        customerData.contacts = [];
        console.log('No contacts, set to empty array');
      }

      console.log('Final contacts:', customerData.contacts);

      setCustomer(customerData);
      setLoading(false);
    } catch (error) {
      console.error('Müşteri yüklenemedi:', error);
      setLoading(false);
    }
  };

  // Personal info handlers
  const handleEditInfo = () => {
    setInfoForm({
      gender: customer.gender || '',
      marital_status: customer.marital_status || '',
      children_count: customer.children_count || 0,
      birth_date: customer.birth_date || '',
      education_status: customer.education_status || '',
      nationality: customer.nationality || 'TC',
      profession: customer.profession || ''
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    try {
      await api.put(`/customers/${id}`, infoForm);
      await fetchCustomer();
      setEditingInfo(false);
    } catch (error) {
      console.error('Bilgiler güncellenemedi:', error);
      alert('Bilgiler güncellenirken bir hata oluştu');
    }
  };

  // Registration info handlers
  const handleEditRegistration = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
    setRegistrationForm({
      source_of_registration: customer.source_of_registration || '',
      source_of_hearing: customer.source_of_hearing || '',
      sales_representative_id: customer.sales_representative_id || ''
    });
    setEditingRegistration(true);
  };

  const handleSaveRegistration = async () => {
    try {
      await api.put(`/customers/${id}`, registrationForm);
      await fetchCustomer();
      setEditingRegistration(false);
    } catch (error) {
      console.error('Kayıt bilgileri güncellenemedi:', error);
      alert('Kayıt bilgileri güncellenirken bir hata oluştu');
    }
  };

  // Contact handlers
  const handleAddContact = () => {
    setEditingContact(null);
    setContactForm({ type: 'GSM', value: '' });
    setShowContactModal(true);
  };

  const handleEditContact = (contact, index) => {
    setEditingContact(index);
    setContactForm(contact);
    setShowContactModal(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.value.trim()) {
      alert('Lütfen iletişim bilgisini giriniz');
      return;
    }

    try {
      const contacts = [...(customer.contacts || [])];
      if (editingContact !== null) {
        contacts[editingContact] = contactForm;
      } else {
        contacts.push(contactForm);
      }

      console.log('Current contacts:', customer.contacts);
      console.log('New contacts array:', contacts);
      console.log('Sending to backend:', JSON.stringify(contacts));

      // Backend'e JSON string olarak gönder
      const payload = {
        contacts: JSON.stringify(contacts)
      };

      console.log('Payload:', payload);

      const response = await api.put(`/customers/${id}`, payload);

      console.log('Contact save response:', response.data);

      // Başarılı, modal'ı kapat ve listeyi yenile
      setShowContactModal(false);
      setContactForm({ type: 'GSM', value: '' });
      setEditingContact(null);

      // Müşteri verisini yenile
      await fetchCustomer();

    } catch (error) {
      console.error('İletişim bilgisi kaydedilemedi:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);

      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Bilinmeyen hata';

      alert('İletişim bilgisi kaydedilirken bir hata oluştu:\n' + errorMessage);
    }
  };

  const handleDeleteContact = async (index) => {
    const result = await showDeleteConfirm('Bu iletişim bilgisi');
    if (!result.isConfirmed) {
      return;
    }
    try {
      const contacts = customer.contacts.filter((_, i) => i !== index);

      console.log('Deleting contact at index:', index);
      console.log('Remaining contacts:', contacts);

      await api.put(`/customers/${id}`, {
        contacts: JSON.stringify(contacts)
      });

      await fetchCustomer();
    } catch (error) {
      console.error('İletişim bilgisi silinemedi:', error);
      console.error('Error details:', error.response?.data);

      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Bilinmeyen hata';

      alert('İletişim bilgisi silinirken bir hata oluştu:\n' + errorMessage);
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    setAddingNote(true);
    try {
      if (editingNoteIndex !== null) {
        // Düzenleme modu
        const notes = customer.description ? customer.description.split('\n\n') : [];
        const timestamp = new Date().toLocaleString('tr-TR');
        notes[notes.length - 1 - editingNoteIndex] = `[${timestamp}] ${noteText}`;
        const updatedDescription = notes.join('\n\n');

        await api.put(`/customers/${id}`, {
          description: updatedDescription
        });

        setEditingNoteIndex(null);
      } else {
        // Yeni not ekleme
        const currentDescription = customer.description || '';
        const timestamp = new Date().toLocaleString('tr-TR');
        const newNote = `[${timestamp}] ${noteText}`;
        const updatedDescription = currentDescription
          ? `${currentDescription}\n\n${newNote}`
          : newNote;

        await api.put(`/customers/${id}`, {
          description: updatedDescription
        });
      }

      await fetchCustomer();
      setNoteText('');
    } catch (error) {
      console.error('Not kaydedilemedi:', error);
      console.error('Error details:', error.response?.data);
      alert('Not kaydedilirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setAddingNote(false);
    }
  };

  const handleEditNote = (index, content) => {
    setEditingNoteIndex(index);
    setNoteText(content);
    // Scroll to textarea
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteNote = async (index) => {
    const result = await showDeleteConfirm('Bu not');
    if (!result.isConfirmed) {
      return;
    }

    try {
      const notes = customer.description ? customer.description.split('\n\n') : [];
      notes.splice(notes.length - 1 - index, 1);
      const updatedDescription = notes.join('\n\n');

      await api.put(`/customers/${id}`, {
        description: updatedDescription
      });

      await fetchCustomer();
    } catch (error) {
      console.error('Not silinemedi:', error);
      alert('Not silinirken bir hata oluştu');
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteIndex(null);
    setNoteText('');
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  if (!customer) {
    return <div className="text-center py-8">Müşteri bulunamadı</div>;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Nitelikli': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'Yeni': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'Negatif': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const meetingColumns = [
    { header: 'Tarih', render: (row) => new Date(row.date).toLocaleDateString('tr-TR') },
    { header: 'Tip', accessor: 'type', render: (row) => formatStatus(row.type) },
    { header: 'Temsilci', render: (row) => row.user?.name || '-' },
    {
      header: 'Durum',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.is_completed
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}>
          {row.is_completed ? 'Tamamlandı' : 'Bekliyor'}
        </span>
      )
    },
  ];

  const salesColumns = [
    {
      header: 'Portföy',
      render: (row) => (
        <span
          className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
          onClick={(e) => {
            e.stopPropagation();
            if (row.portfolio?.id) navigate(`/portfolios/${row.portfolio.id}`);
          }}
        >
          {row.portfolio?.title}
        </span>
      )
    },
    { header: 'Fiyat', render: (row) => `${row.final_sale_price?.toLocaleString('tr-TR')} ₺` },
    { header: 'Tarih', render: (row) => new Date(row.sale_date).toLocaleDateString('tr-TR') },
  ];

  const tabs = [
    { id: 'info', label: 'Bilgiler' },
    { id: 'preferences', label: 'Gayrimenkul Tercihleri' },
    { id: 'portfolios', label: 'İlgilendiği Portföyler' },
    { id: 'meetings', label: 'Görüşmeler' },
    { id: 'sales', label: 'Satışlar' },
    { id: 'notes', label: 'Notlar' },
  ];

  return (
    <div>
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Button variant="secondary" size="sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button size="sm" variant="secondary" onClick={() => navigate(`/meetings/new?customer=${id}`)} className="flex-1 sm:flex-initial">
            <span className="hidden sm:inline">Görüşme Ekle</span>
            <span className="sm:hidden">Görüşme</span>
          </Button>
          <Button size="sm" onClick={() => navigate(`/customers/${id}/edit`)} className="flex-1 sm:flex-initial">
            <Edit size={16} />
            <span className="hidden sm:inline">Düzenle</span>
          </Button>
        </div>
      </div>

      {/* Header */}
      <Card className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User size={32} className="sm:w-10 sm:h-10 text-primary-600" />
          </div>
          <div className="flex-1 w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                {customer.name} {customer.surname}
              </h1>
              <div className="flex gap-2">
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(customer.status)}`}>
                  {formatStatus(customer.status)}
                </span>
                <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {formatStatus(customer.customer_type)}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-sm sm:text-base text-gray-600">
              {customer.contacts?.find(c => c.type === 'GSM') && (
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>{customer.contacts.find(c => c.type === 'GSM').value}</span>
                </div>
              )}
              {customer.contacts?.find(c => c.type === 'E-Posta') && (
                <div className="flex items-center gap-2">
                  <Mail size={16} />
                  <span className="truncate">{customer.contacts.find(c => c.type === 'E-Posta').value}</span>
                </div>
              )}
              {customer.profession && (
                <div className="flex items-center gap-2">
                  <Briefcase size={16} />
                  <span>{customer.profession}</span>
                </div>
              )}
            </div>
          </div>
          {customer.budget && (
            <div className="w-full sm:w-auto text-left sm:text-right border-t sm:border-t-0 dark:border-gray-700 pt-3 sm:pt-0">
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Bütçe</div>
              <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
                {customer.budget?.toLocaleString('tr-TR')} ₺
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-4 sm:space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card
            title="Kişisel Bilgiler"
            className="lg:col-span-2"
            action={
              !editingInfo ? (
                <button
                  onClick={handleEditInfo}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Düzenle"
                >
                  <Edit size={16} />
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingInfo(false)}
                    className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSaveInfo}
                    className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
                  >
                    <Save size={14} />
                    Kaydet
                  </button>
                </div>
              )
            }
          >
            {!editingInfo ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Cinsiyet</p>
                  <p className="font-semibold">{formatStatus(customer.gender) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Medeni Durum</p>
                  <p className="font-semibold">{formatStatus(customer.marital_status) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Çocuk Sayısı</p>
                  <p className="font-semibold">{customer.children_count || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Doğum Tarihi</p>
                  <p className="font-semibold">
                    {customer.birth_date ? new Date(customer.birth_date).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Eğitim Durumu</p>
                  <p className="font-semibold">{customer.education_status || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Uyruk</p>
                  <p className="font-semibold">{customer.nationality || 'TC'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Meslek</p>
                  <p className="font-semibold">{customer.profession || '-'}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Cinsiyet"
                  value={infoForm.gender}
                  onChange={(e) => setInfoForm({ ...infoForm, gender: e.target.value })}
                  options={[
                    { value: '', label: 'Seçiniz' },
                    { value: 'male', label: 'Erkek' },
                    { value: 'female', label: 'Kadın' },
                    { value: 'other', label: 'Diğer' }
                  ]}
                />
                <Select
                  label="Medeni Durum"
                  value={infoForm.marital_status}
                  onChange={(e) => setInfoForm({ ...infoForm, marital_status: e.target.value })}
                  options={[
                    { value: '', label: 'Seçiniz' },
                    { value: 'single', label: 'Bekar' },
                    { value: 'married', label: 'Evli' },
                    { value: 'divorced', label: 'Boşanmış' },
                    { value: 'widowed', label: 'Dul' }
                  ]}
                />
                <Input
                  label="Çocuk Sayısı"
                  type="number"
                  value={infoForm.children_count}
                  onChange={(e) => setInfoForm({ ...infoForm, children_count: e.target.value })}
                />
                <Input
                  label="Doğum Tarihi"
                  type="date"
                  value={infoForm.birth_date}
                  onChange={(e) => setInfoForm({ ...infoForm, birth_date: e.target.value })}
                />
                <Input
                  label="Eğitim Durumu"
                  value={infoForm.education_status}
                  onChange={(e) => setInfoForm({ ...infoForm, education_status: e.target.value })}
                />
                <Input
                  label="Uyruk"
                  value={infoForm.nationality}
                  onChange={(e) => setInfoForm({ ...infoForm, nationality: e.target.value })}
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Meslek"
                    value={infoForm.profession}
                    onChange={(e) => setInfoForm({ ...infoForm, profession: e.target.value })}
                  />
                </div>
              </div>
            )}
          </Card>

          <div className="space-y-6">
            <Card
              title="İletişim Bilgileri"
              action={
                <button
                  onClick={handleAddContact}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  title="Ekle"
                >
                  <Plus size={16} />
                </button>
              }
            >
              <div className="space-y-3">
                {customer.contacts && customer.contacts.length > 0 ? (
                  customer.contacts.map((contact, index) => (
                    <div key={index} className="flex items-start gap-3 group">
                      <div className="bg-gray-100 p-2 rounded">
                        {contact.type === 'E-Posta' ? <Mail size={16} /> :
                          contact.type === 'Adres' ? <MapPin size={16} /> :
                            <Phone size={16} />}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">{contact.type}</p>
                        <p className="font-medium">{contact.value}</p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditContact(contact, index)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Düzenle"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(index)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">İletişim bilgisi yok</p>
                )}
              </div>
            </Card>

            <Card
              title="Kayıt Bilgileri"
              action={
                !editingRegistration ? (
                  <button
                    onClick={handleEditRegistration}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                    title="Düzenle"
                  >
                    <Edit size={16} />
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingRegistration(false)}
                      className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleSaveRegistration}
                      className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
                    >
                      <Save size={14} />
                      Kaydet
                    </button>
                  </div>
                )
              }
            >
              {!editingRegistration ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Kayıt Yöntemi</p>
                    <p className="font-semibold">{customer.source_of_registration || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Nereden Duydu</p>
                    <p className="font-semibold">{customer.source_of_hearing || '-'}</p>
                  </div>
                  {customer.sales_representative && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Satış Temsilcisi</p>
                      <p className="font-semibold text-primary-600">{customer.sales_representative.name}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <Select
                    label="Kayıt Yöntemi"
                    value={registrationForm.source_of_registration}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, source_of_registration: e.target.value })}
                    options={[
                      { value: '', label: 'Seçiniz' },
                      { value: 'Web Sitesi', label: 'Web Sitesi' },
                      { value: 'Telefon', label: 'Telefon' },
                      { value: 'Ofis Ziyareti', label: 'Ofis Ziyareti' },
                      { value: 'Sosyal Medya', label: 'Sosyal Medya' },
                      { value: 'Referans', label: 'Referans' },
                      { value: 'Diğer', label: 'Diğer' }
                    ]}
                  />
                  <Select
                    label="Nereden Duydu"
                    value={registrationForm.source_of_hearing}
                    onChange={(e) => setRegistrationForm({ ...registrationForm, source_of_hearing: e.target.value })}
                    options={[
                      { value: '', label: 'Seçiniz' },
                      { value: 'Google', label: 'Google' },
                      { value: 'Facebook', label: 'Facebook' },
                      { value: 'Instagram', label: 'Instagram' },
                      { value: 'Arkadaş Tavsiyesi', label: 'Arkadaş Tavsiyesi' },
                      { value: 'Gazete/Dergi', label: 'Gazete/Dergi' },
                      { value: 'Billboard', label: 'Billboard' },
                      { value: 'Diğer', label: 'Diğer' }
                    ]}
                  />
                  {canAssign('customers') && (
                    <Select
                      label="Satış Temsilcisi"
                      value={registrationForm.sales_representative_id}
                      onChange={(e) => setRegistrationForm({ ...registrationForm, sales_representative_id: e.target.value })}
                      options={[
                        { value: '', label: 'Seçiniz' },
                        ...users.map(u => ({ value: u.id, label: u.name }))
                      ]}
                    />
                  )}
                </div>
              )}
            </Card>
          </div>

          {customer.description && (
            <Card title="Açıklama" className="lg:col-span-3">
              <p className="text-gray-700 whitespace-pre-wrap">{customer.description}</p>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'preferences' && (
        <PropertyPreferences customerId={id} />
      )}

      {activeTab === 'portfolios' && (
        <InterestedPortfolios customerId={id} />
      )}

      {activeTab === 'meetings' && (
        <Card
          title="Görüşme Geçmişi"
          noPadding
        >
          {customer.meetings && customer.meetings.length > 0 ? (
            <Table
              columns={meetingColumns}
              data={customer.meetings}
              onView={(row) => navigate(`/meetings/${row.id}`)}
            />
          ) : (
            <div className="p-6 text-center text-gray-500">
              Henüz görüşme kaydı yok
            </div>
          )}
        </Card>
      )}

      {activeTab === 'sales' && (
        <Card title="Satış Geçmişi" noPadding>
          {customer.sales && customer.sales.length > 0 ? (
            <Table
              columns={salesColumns}
              data={customer.sales}
            />
          ) : (
            <div className="p-6 text-center text-gray-500">
              Henüz satış kaydı yok
            </div>
          )}
        </Card>
      )}

      {activeTab === 'notes' && (
        <Card title="Müşteri Notları">
          {/* Add/Edit Note Form */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            {editingNoteIndex !== null && (
              <div className="mb-2 flex items-center gap-2 text-sm text-blue-600">
                <Edit size={14} />
                <span>Not düzenleniyor...</span>
              </div>
            )}
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={editingNoteIndex !== null ? "Notu düzenle..." : "Yeni not ekle..."}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={3}
            />
            <div className="mt-2 flex justify-end gap-2">
              {editingNoteIndex !== null && (
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
                >
                  İptal
                </button>
              )}
              <button
                onClick={handleAddNote}
                disabled={!noteText.trim() || addingNote}
                className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingNoteIndex !== null ? <Save size={14} /> : <Plus size={14} />}
                {addingNote ? 'Kaydediliyor...' : editingNoteIndex !== null ? 'Güncelle' : 'Not Ekle'}
              </button>
            </div>
          </div>

          {/* Notes List - Description alanından parse et */}
          <div className="space-y-4">
            {customer.description ? (
              customer.description.split('\n\n').reverse().map((note, index) => {
                // [Tarih] Not formatını parse et
                const match = note.match(/^\[(.*?)\]\s(.+)$/s);
                if (match) {
                  const [, timestamp, content] = match;
                  return (
                    <div key={index} className="border-l-4 border-primary-500 pl-4 py-2 bg-gray-50 rounded-r group">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <MessageSquare size={16} className="text-gray-400" />
                          <span className="text-xs text-gray-500">{timestamp}</span>
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditNote(index, content)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Düzenle"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Sil"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{content}</p>
                    </div>
                  );
                } else {
                  // Eski format notlar için
                  return (
                    <div key={index} className="border-l-4 border-gray-300 pl-4 py-2 bg-gray-50 rounded-r group">
                      <div className="flex justify-between items-start">
                        <p className="text-gray-700 whitespace-pre-wrap flex-1">{note}</p>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditNote(index, note)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Düzenle"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(index)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Sil"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
              })
            ) : (
              <p className="text-center text-gray-500 py-4">Henüz not eklenmemiş</p>
            )}
          </div>
        </Card>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <Modal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          title={editingContact !== null ? 'İletişim Bilgisi Düzenle' : 'İletişim Bilgisi Ekle'}
        >
          <div className="space-y-4">
            <Select
              label="Tip"
              value={contactForm.type}
              onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
              options={[
                { value: 'GSM', label: 'GSM' },
                { value: 'Telefon', label: 'Telefon' },
                { value: 'E-Posta', label: 'E-Posta' },
                { value: 'Adres', label: 'Adres' }
              ]}
            />
            <Input
              label="Değer"
              value={contactForm.value}
              onChange={(e) => setContactForm({ ...contactForm, value: e.target.value })}
              placeholder={contactForm.type === 'E-Posta' ? 'ornek@email.com' : contactForm.type === 'Adres' ? 'Adres bilgisi' : '0555 555 55 55'}
            />
            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={() => setShowContactModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={handleSaveContact}
                className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 rounded-lg flex items-center gap-2"
              >
                <Save size={14} />
                Kaydet
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default CustomerDetail;
